import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { Tooltip, Menu, MenuItem, ListItemText, Typography } from "@material-ui/core";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import SettingsPhoneIcon from "@material-ui/icons/SettingsPhone";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import BusinessIcon from "@material-ui/icons/Business";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ImportContactsModal from "../../components/ImportContactsModal";
import ContactAvatar from "../../components/ContactAvatar";
import ClienteQuickCreateModal from "../../components/ClienteQuickCreateModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { visibleWhatsappsForUser } from "../../helpers/canAccessTicket";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import ConnectionSelectDialog from "../../components/ConnectionSelectDialog";
import { SocketContext } from "../../context/Socket/SocketContext";
import useSettings from "../../hooks/useSettings";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { exportToExcel } from "../../utils/exportToExcel";
import { exportToCsv } from "../../utils/exportToCsv";

const CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY = "clienteFieldRequirementsV1";
const DEFAULT_CONTACTS_REQUIRED_FIELDS = {
  name: true,
  number: true,
  email: false,
};

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // Contato já existe na lista - atualiza
      state[contactIndex] = contact;
      return [...state];
    } else {
      // Contato não existe na lista - NÃO adiciona automaticamente
      // O contato só deve aparecer se corresponder ao filtro atual
      // A lógica de filtro é feita no backend, então ignoramos novos contatos via WebSocket
      return state;
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);
  const { connectionScopeEnabled } = useContext(SettingsContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkMode, setLinkMode] = useState("existing");
  const [linkingContact, setLinkingContact] = useState(null);
  const [clienteSearchParam, setClienteSearchParam] = useState("");
  const [clienteOptions, setClienteOptions] = useState([]);
  const [selectedClienteOption, setSelectedClienteOption] = useState(null);
  const [loadingClienteOptions, setLoadingClienteOptions] = useState(false);
  const [clienteQuickCreateOpen, setClienteQuickCreateOpen] = useState(false);

  // Seletor de conexão WhatsApp
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [connDialogOpen, setConnDialogOpen] = useState(false);

  // Exportação da base (menu XLSX/CSV)
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportContacts = async (format) => {
    setExportMenuAnchorEl(null);
    setExporting(true);
    try {
      // Base completa respeitando a busca da tela — não o array paginado em
      // memória, que só tem as páginas já roladas.
      const { data } = await api.get("/contacts/", {
        params: { searchParam, exportAll: true, contactsOnly: true },
        timeout: 120000,
      });
      const fetched = Array.isArray(data?.contacts) ? data.contacts : [];
      if (!fetched.length) {
        toast.info(i18n.t("contacts.toasts.exportEmpty"));
        return;
      }
      // Mesmas colunas (e ordem) do modelo de importação — o arquivo exportado
      // é re-importável direto pelo modal "De arquivo".
      const rows = fetched.map((c) => ({
        nome: c.name || "",
        numero: c.number || "",
        email: c.email || "",
        telefone: c.ddd_telefone
          ? `(${c.ddd_telefone}) ${c.telefone || ""}`.trim()
          : c.telefone || "",
        email_secundario: c.email_secundario || "",
        linkedin: c.linkedin || "",
      }));
      const filename = `contatos-${new Date().toISOString().slice(0, 10)}`;
      if (format === "xlsx") {
        exportToExcel(rows, filename, i18n.t("contacts.title"));
      } else {
        exportToCsv(rows, filename);
      }
      if (data?.hasMore) {
        toast.warn(i18n.t("contacts.toasts.exportLimited"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const socketManager = useContext(SocketContext);
  const { getAll: getAllSettings } = useSettings();
  const [contactFieldRequirements, setContactFieldRequirements] = useState(
    DEFAULT_CONTACTS_REQUIRED_FIELDS
  );

  // Carrega conexões disponíveis
  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp", { params: { companyId: user.companyId, session: 0 } });
        setWhatsapps(visibleWhatsappsForUser(user, data || [], connectionScopeEnabled));
      } catch (err) {
        console.error("Erro ao carregar conexões:", err);
      }
    };
    fetchWhatsapps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.companyId, user.whatsappId, user.whatsapps?.length, connectionScopeEnabled]);

  useEffect(() => {
    const loadFieldRequirements = async () => {
      try {
        const settings = await getAllSettings();
        if (!Array.isArray(settings)) {
          return;
        }

        const setting = settings.find(
          (item) => item.key === CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY
        );

        if (!setting?.value) {
          return;
        }

        const parsed = JSON.parse(setting.value);
        setContactFieldRequirements((prevState) => ({
          ...prevState,
          ...(parsed?.contacts || {}),
        }));
      } catch (error) {
        console.error("Erro ao carregar campos obrigatórios de contatos:", error);
      }
    };

    loadFieldRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleOpenLinkClienteModal = (contact) => {
    setLinkingContact(contact);
    setLinkMode("existing");
    setClienteSearchParam("");
    setClienteOptions([]);
    setSelectedClienteOption(null);
    setClienteQuickCreateOpen(false);
    setLinkModalOpen(true);
  };

  const handleCloseLinkClienteModal = () => {
    setLinkModalOpen(false);
    setLinkingContact(null);
    setLinkMode("existing");
    setClienteSearchParam("");
    setClienteOptions([]);
    setSelectedClienteOption(null);
    setClienteQuickCreateOpen(false);
  };

  const handleLinkToExistingCliente = async () => {
    if (!linkingContact?.id) {
      return;
    }

    if (!selectedClienteOption?.CODCLI) {
      toast.warning(i18n.t("contacts.linkModal.selectCliente"));
      return;
    }

    try {
      const { data } = await api.post(`/contacts/${linkingContact.id}/link-cliente`, {
        clienteId: selectedClienteOption.CODCLI
      });

      dispatch({
        type: "UPDATE_CONTACTS",
        payload: {
          ...linkingContact,
          linkedClienteId: Number(selectedClienteOption.CODCLI),
          linkedClienteNome: selectedClienteOption.NOMECLI
        }
      });

      if (data?.alreadyLinked) {
        toast.info(i18n.t("linkClienteModal.alreadyLinked"));
      } else {
        toast.success(i18n.t("linkClienteModal.success"));
      }

      handleCloseLinkClienteModal();
    } catch (err) {
      toastError(err);
    }
  };

  // O cadastro rápido não se fecha sozinho no sucesso — quem fecha é este
  // handler. O cliente novo entra nas opções e vira a seleção; o vínculo em si
  // continua acontecendo no botão "Vincular"
  const handleClienteQuickCreated = (novoCliente) => {
    setClienteQuickCreateOpen(false);
    if (!novoCliente?.CODCLI) return;

    setClienteOptions((current) =>
      current.some((c) => String(c.CODCLI) === String(novoCliente.CODCLI))
        ? current
        : [novoCliente, ...current]
    );
    setSelectedClienteOption(novoCliente);
  };

  const handleGoToNewClienteWithContact = () => {
    if (!linkingContact?.id) {
      toast.warning(i18n.t("contacts.linkModal.invalidContact"));
      return;
    }

    history.push("/clientes/novo", {
      preselectedContact: {
        id: linkingContact.id,
        name: linkingContact.name,
        number: linkingContact.number,
        email: linkingContact.email
      }
    });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  useEffect(() => {
    if (!linkModalOpen || linkMode !== "existing") {
      return;
    }

    const debounce = setTimeout(async () => {
      setLoadingClienteOptions(true);
      try {
        const { data } = await api.get("/clientes", {
          params: {
            searchParam: clienteSearchParam,
            pageNumber: 1
          }
        });
        setClienteOptions(data?.clientes || []);
      } catch (error) {
        toastError(error);
      } finally {
        setLoadingClienteOptions(false);
      }
    }, 350);

    return () => clearTimeout(debounce);
  }, [linkModalOpen, linkMode, clienteSearchParam]);

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        initialWhatsappId={selectedWhatsappId}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
        fieldRequirements={contactFieldRequirements}
      ></ContactModal>
      <ConfirmationModal
        title={`${i18n.t("contacts.confirmationModal.deleteTitle")} ${
          deletingContact?.name
        }?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
      >
        {i18n.t("contacts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ImportContactsModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        whatsapps={whatsapps}
        onImported={() => history.go(0)}
      />
      <Dialog
        open={linkModalOpen}
        onClose={handleCloseLinkClienteModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{i18n.t("linkClienteModal.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {i18n.t("contacts.linkModal.contactLabel", {
              name: linkingContact?.name || "-",
            })}
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginBottom: 16 }}>
            <InputLabel id="link-mode-label">
              {i18n.t("contacts.linkModal.linkType")}
            </InputLabel>
            <Select
              labelId="link-mode-label"
              value={linkMode}
              onChange={(event) => {
                setLinkMode(event.target.value);
                setSelectedClienteOption(null);
              }}
              label={i18n.t("contacts.linkModal.linkType")}
            >
              <MenuItem value="existing">
                {i18n.t("contacts.linkModal.existingCliente")}
              </MenuItem>
              <MenuItem value="new">
                {i18n.t("contacts.linkModal.newCliente")}
              </MenuItem>
            </Select>
          </FormControl>

          {linkMode === "existing" && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Autocomplete
                  options={clienteOptions}
                  loading={loadingClienteOptions}
                  value={selectedClienteOption}
                  onChange={(event, value) => setSelectedClienteOption(value)}
                  // Busca server-side (inclui CNPJ/CPF/e-mail, que nao estao no
                  // label) - sem isso o Autocomplete refiltra pelo label
                  filterOptions={(opts) => opts}
                  getOptionLabel={(option) => option?.NOMECLI || ""}
                  getOptionSelected={(option, value) => option.CODCLI === value?.CODCLI}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n.t("linkClienteModal.searchLabel")}
                      variant="outlined"
                      onChange={(event) => setClienteSearchParam(event.target.value || "")}
                      helperText={i18n.t("contacts.linkModal.searchHelper")}
                    />
                  )}
                />
              </div>
              <Tooltip title={i18n.t("linkClienteModal.newCliente")}>
                <IconButton
                  color="primary"
                  onClick={() => setClienteQuickCreateOpen(true)}
                  style={{ marginTop: 8 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}

          {linkMode === "new" && (
            <Typography variant="body2" color="textSecondary">
              {i18n.t("contacts.linkModal.newClienteInfo")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkClienteModal}>
            {i18n.t("common.cancel")}
          </Button>
          {linkMode === "existing" ? (
            <Button
              color="primary"
              variant="contained"
              onClick={handleLinkToExistingCliente}
            >
              {i18n.t("linkClienteModal.buttons.link")}
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              onClick={handleGoToNewClienteWithContact}
            >
              {i18n.t("linkClienteModal.newCliente")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Irmão do Dialog de vínculo: dentro dele o ESC do cadastro rápido
          fecharia também o modal de vínculo */}
      <ClienteQuickCreateModal
        open={clienteQuickCreateOpen}
        onClose={() => setClienteQuickCreateOpen(false)}
        onSave={handleClienteQuickCreated}
        initialName={clienteSearchParam || linkingContact?.name || ""}
      />
      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={selectedWhatsappId
            ? i18n.t("contacts.connectionTooltip.selected", {
                name:
                  whatsapps.find((w) => w.id === selectedWhatsappId)?.name ||
                  i18n.t("contacts.connectionTooltip.selectedFallback"),
              })
            : i18n.t("contacts.connectionTooltip.select")
          }>
            <IconButton
              onClick={() => setConnDialogOpen(true)}
              style={{
                marginLeft: 8,
                color: selectedWhatsappId ? "#25d366" : "gray",
              }}
            >
              <SettingsPhoneIcon />
            </IconButton>
          </Tooltip>
          <ConnectionSelectDialog
            open={connDialogOpen}
            onClose={() => setConnDialogOpen(false)}
            whatsapps={whatsapps}
            selectedWhatsappId={selectedWhatsappId}
            allowAutomatic
            onSelect={(w) => setSelectedWhatsappId(w ? w.id : "")}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setImportModalOpen(true)}
          >
            {i18n.t("contacts.buttons.import")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactModal}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={exporting}
            onClick={(e) => setExportMenuAnchorEl(e.currentTarget)}
          >
            {i18n.t("contacts.buttons.export")}
          </Button>
          <Menu
            anchorEl={exportMenuAnchorEl}
            open={Boolean(exportMenuAnchorEl)}
            onClose={() => setExportMenuAnchorEl(null)}
            keepMounted
          >
            <MenuItem onClick={() => handleExportContacts("xlsx")}>
              {i18n.t("contacts.exportMenu.xlsx")}
            </MenuItem>
            <MenuItem onClick={() => handleExportContacts("csv")}>
              {i18n.t("contacts.exportMenu.csv")}
            </MenuItem>
          </Menu>

        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell style={{ paddingRight: 0 }}>
                    <ContactAvatar contact={contact} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{contact.name}</Typography>
                    {contact.linkedClienteNome && (
                      <Button
                        size="small"
                        color="primary"
                        style={{
                          padding: 0,
                          minWidth: 0,
                          textTransform: "none",
                          justifyContent: "flex-start"
                        }}
                        onClick={() => {
                          if (!contact.linkedClienteId) {
                            return;
                          }

                          history.push(`/clientes/${contact.linkedClienteId}`);
                        }}
                      >
                        {i18n.t("contacts.linkedCliente", {
                          name: contact.linkedClienteNome,
                        })}
                        {Array.isArray(contact.linkedClientes) &&
                          contact.linkedClientes.length > 1 &&
                          ` +${contact.linkedClientes.length - 1}`}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {/* Só exibe se não for grupo e se o número parecer um telefone válido (até 15 dígitos) */}
                    {!contact.isGroup && contact.number && contact.number.length <= 15 ? contact.number : ""}
                  </TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip title={i18n.t("linkClienteModal.title")}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenLinkClienteModal(contact)}
                      >
                        <BusinessIcon />
                      </IconButton>
                    </Tooltip>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
